import { UUID } from '@cmg/common';

import { AttestationForm, CertificateType, OfferingType } from '../../../../../../../graphql';
import { useSideBar_OfferingSellingRestrictions_OfferingQuery } from '../../../../../components/side-bar/graphql';
import {
  OrderBookPersonaType,
  useIdentifyOrderBookPersona,
} from '../../../../hooks/useIdentifyOrderBookPersona';
import { Compliance_BuySide_AttestationQuery } from '../buy-side/graphql';
import { Compliance_Syndicate_AttestationQuery } from '../syndicate/graphql';
import { useBuySideCertificatesModel } from './buy-side/useCertificatesBuySideModel';
import { getCertificateType, isReg144A } from './Certificates.model';
import { useSalesAndTradingCertificatesModel } from './sale-and-trading/useCertificatesSalesAndTradingModel';
import { useSyndicateCertificatesModel } from './syndicate/useCertificatesSyndicateModel';

export type Attestation =
  | Compliance_Syndicate_AttestationQuery['indicationAttestation']
  | Omit<Compliance_BuySide_AttestationQuery['myAttestation'], 'attestationForm'>;

export type ComplianceWarning = {
  hasComplianceWarning: boolean;
  canByPassComplianceWarning: boolean;
  message: string | null;
  redirectUrl: string | null;
  tooltipMessage: string | null;
};

export type CertificateModel = {
  canRead: boolean;
  attestation: Attestation | null;
  attestationForm: AttestationForm | null;
  loading: boolean;
  offeringType?: OfferingType;
  warning: ComplianceWarning;
  certificateType?: CertificateType | null;
};

export const certificateModelDefaults: CertificateModel = {
  canRead: false,
  attestation: null,
  attestationForm: null,
  loading: false,
  offeringType: undefined,
  certificateType: undefined,
  warning: {
    hasComplianceWarning: false,
    canByPassComplianceWarning: false,
    message: null,
    redirectUrl: null,
    tooltipMessage: null,
  },
};

export type Props = {
  offeringId: UUID;
  cmgEntityKey: string | undefined;
};

export type PersonaProps = {
  type: OrderBookPersonaType;
};
/*
The useComplianceCertificatesModel is in charge of handling all the compliance certificates data, and also helps us have a 
centralized place that we can use to decide what actions we can do with the certificates section.

For example: canRead: This will help decide if compliance certificates should be displayed or not, 
all the logic for deciding to display or not should be inside the property
*/
export const useComplianceCertificatesModel = ({
  offeringId,
  cmgEntityKey,
}: Props): CertificateModel => {
  const { data } = useSideBar_OfferingSellingRestrictions_OfferingQuery({
    variables: { offeringId },
    skip: !offeringId,
  });

  const [offering] = data?.publishedOfferings?.items ?? [];

  const instruments = offering?.instruments ?? [];
  const offeringType = offering?.type;
  const isReg144AValue = instruments[0] ? isReg144A(instruments[0]) : false;
  const certificateType = getCertificateType(offeringType, isReg144AValue);

  const { type } = useIdentifyOrderBookPersona({ offeringId });
  const syndicateModel = useSyndicateCertificatesModel({
    type,
    offeringId,
    cmgEntityKey,
    certificateType,
    offeringType,
  });
  const salesAndTradingModel = useSalesAndTradingCertificatesModel({
    type,
    offeringId,
    cmgEntityKey,
    certificateType,
    isReg144A: isReg144AValue,
    offeringType,
  });
  const buySideModel = useBuySideCertificatesModel({
    type,
    offeringId,
    certificateType,
    hasActiveRelationship: true,
  });

  switch (type) {
    case OrderBookPersonaType.SYNDICATE: {
      return syndicateModel;
    }
    case OrderBookPersonaType.SALES_AND_TRADING: {
      return salesAndTradingModel;
    }
    case OrderBookPersonaType.BUY_SIDE: {
      return buySideModel;
    }
    default: {
      return certificateModelDefaults;
    }
  }
};
